import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Download, FileText, ChevronRight, Mail, Copy } from 'lucide-react';
import { MoldableIcon, BiocompatibleIcon, MechanicalIcon, ReconstructionIcon, AbsorptionIcon, RegenerationIcon, BioactiveIcon, NonToxicIcon, DefectSpanIcon, NonAnimalIcon, StorageIcon } from '../components/FlexiOssCinematic/PropertyIcons';
import { Button } from '../components/ui/button';
import SubpageLayout from '../components/FlexiOssCinematic/SubpageLayout';
import ReadAloud from '../components/ReadAloud';
import PhilHealthCallout from '../components/PhilHealthCallout';
import SocialShare from '../components/FlexiOssCinematic/SocialShare';
import StructuredData from '../components/StructuredData';
import { orthoSchema } from '../data/productSchema';
import { HorizontalBorderStack, EditorialColumnSplit } from '../components/editorial/EditorialLayouts';
import { ProductPropertiesEditorial, ProductCertifications, ProductPublications } from '../components/product/ProductSections';
import { SITE_URL } from '../lib/siteConfig';
import { toast } from 'sonner';

const ACCENT = '#3b82f6';

const API = process.env.REACT_APP_BACKEND_URL;
const dl = (url, name) => {
  const href = /^https?:\/\//i.test(url) && (!API || !url.startsWith(API))
    ? `${API}/api/download?url=${encodeURIComponent(url)}&name=${encodeURIComponent(name || 'FlexiOss.pdf')}`
    : url;
  const a = document.createElement('a');
  a.href = href; a.target = '_blank'; a.rel = 'noopener noreferrer';
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
};

const properties = [
  { icon: MoldableIcon, title: 'Flexible After Soaking', desc: 'Plastic and bendable — fits individual bone defects by cutting or bending during operation' },
  { icon: BiocompatibleIcon, title: 'Bio-Compatible', desc: 'Compatible with natural bone tissue for seamless integration' },
  { icon: MechanicalIcon, title: 'Mechanical Parameters', desc: 'Similar to those of cancellous bone — structural support' },
  { icon: ReconstructionIcon, title: 'Supports Bone Reconstruction', desc: 'Actively supports the bone reconstruction process' },
  { icon: AbsorptionIcon, title: 'Absorbs Blood & Medicine', desc: 'Absorbs blood and medicine solutions for enhanced drug delivery' },
  { icon: RegenerationIcon, title: 'Bone Tissue Regeneration', desc: 'Positively affects bone tissue regeneration process' },
  { icon: BioactiveIcon, title: 'Ionic Reactive / Bioactive', desc: 'High ionic reactivity increases mineralization rate' },
  { icon: NonToxicIcon, title: 'Non-Toxic Material', desc: 'Thoroughly tested for safety — no cytotoxicity observed' },
  { icon: DefectSpanIcon, title: 'Up to 7cm Defects', desc: 'Can be used for bone defects of up to 7 cm in length' },
  { icon: NonAnimalIcon, title: 'Non-Animal Material', desc: 'Lowers risk of pathogen transmission and allergic reactions' },
  { icon: StorageIcon, title: 'Easy to Store & Transport', desc: 'Simple storage and transportation requirements' },
];

const publications = [
  { title: 'Characteristics and application of FlexiOss® bone substitute material in trauma-orthopedic patients', authors: 'Lukasiewicz P.', journal: 'Medical Inventi', year: '2025', pdfUrl: 'https://flexioss.pl/wp-content/uploads/2025/06/medical-inventi-flexioss-article-on-characteristics-and-application-Lukasiewicz-modified.pdf' },
  { title: 'Preliminary Results of Surgical Treatment for Enchondroma Using a Novel Bioactive and Osseoconductive HAP/β-Glucan Bone Substitute FlexiOss® — Case Series', authors: 'Kotrych D., Ciechanowicz D., Bielewicz F., Baryluk A., Podsiadlo S., Zietek P.', journal: 'J. Clin. Med. 2025, 14(11), 3738', year: '2025', pdfUrl: 'https://flexioss.pl/wp-content/uploads/2025/05/Preliminary-Results-of-Surgical-Treatment-for-Enchondroma.pdf' },
  { title: 'Behavior of new hydroxyapatite/glucan composite in human serum', authors: 'Borkowski L., Lubek T., Jojczuk M., Nogalski A., Belcarz A., Palka K., Hajnos M., Ginalska G.', journal: 'J. Biomed. Mater. Res. Part B., 2018, 106B(7), 2653-2664', year: '2018', pdfUrl: 'https://flexioss.pl/wp-content/uploads/2017/10/Journal_of_Biomedical_Materials_Research_Part_B__Applied_Biomaterials_2018.pdf' },
  { title: 'Unexpected reaction of new HAp/glucan composite to environmental acidification: Defect or advantage?', authors: 'Borkowski L., Kiernicka M., Belcarz A., Palka K., Hajnos M., Ginalska G.', journal: 'Biomed. Mater. Res. B Appl. Biomater. 2017, 105(5), 1178-1190', year: '2017', externalUrl: 'https://onlinelibrary.wiley.com/doi/abs/10.1002/jbm.b.33646' },
  { title: 'The comparison study of bioactivity between composites containing synthetic non-substituted and carbonate-substituted hydroxyapatite', authors: 'Borkowski L., Sroka-Bartnicka A., et al.', journal: 'Mater. Sci. Eng. C Mater. Biol. Appl. 2016, 62, 260-267', year: '2016', pdfUrl: 'https://flexioss.pl/wp-content/uploads/2021/03/The-comparison-study-of-bioactivity-between-composites-containing.pdf' },
  { title: 'Biphasic mode of antibacterial action of aminoglycoside antibiotics-loaded elastic hydroxyapatite-glucan composite', authors: 'Belcarz A., Zima A., Ginalska G.', journal: 'Int. J. Pharm. 2013, 454, 285-295', year: '2013', pdfUrl: 'https://flexioss.pl/wp-content/uploads/2020/10/EJP-2013.pdf' },
  { title: 'Application of β-1,3-glucan in production of ceramics-based elastic composite for bone repair', authors: 'Belcarz A., Ginalska G., Pycka T., et al.', journal: 'Centr. Eur. J. Biol. 2013, 8, 534-548', year: '2013', pdfUrl: 'https://flexioss.pl/wp-content/uploads/2021/03/Application-of-B-13-glucan-in-production-of.pdf' },
];

const applications = [
  'Comminuted fractures with bone tissue defects',
  'Long bone defect filling and reconstruction (up to 5-7 cm)',
  'Trauma surgery and emergency medicine',
  'Bone pocket filling for tissue regeneration',
  'Post-infective cyst removal and filling',
  'Bone tumour resection defect reconstruction',
  'Osteoporotic bone tissue fracture therapy',
  'Bioactive filling of titanium and polyester scaffolds',
  'Spinal fusion augmentation',
  'Joint revision and arthrodesis procedures',
];

const COMPARE_COLS = [
  { key: 'fo', name: 'FlexiOss® Ortho', sub: 'HAP + curdlan composite', hero: true },
  { key: 'kyphon', name: 'Kyphon® Xpede™ / HV-R®', sub: 'Medtronic · PMMA cement' },
  { key: 'bongros', name: 'Bongros® HA', sub: 'CGbio · HA ceramic' },
  { key: 'excelos', name: 'ExcelOs®', sub: 'CGbio / BioALPHA · β-TCP' },
];

const COMPARE_ROWS = [
  { feat: 'Product form', fo: 'Pre-formed sterile cylinder (double paper-foil pack)', kyphon: 'Two-part cement — powder sachet + liquid bottle', bongros: 'Loose bone chips in jars', excelos: 'Granulate in jars' },
  { feat: 'Available sizes', fo: 'Ø13 mm cylinder — 1, 3 & 5 cm lengths', kyphon: '20 g powder + 9 g liquid', bongros: 'Pre-filled granule jars (multiple volumes)', excelos: '0.3–0.6 mm (ortho) / 0.6–3.0 mm (dental); 5–30 cc' },
  { feat: 'Composition', fo: 'HAP granules (>100 nm) + curdlan sugar binder', kyphon: 'Self-curing PMMA cement (MMA copolymer, barium sulphate, BPO)', bongros: 'Hydroxyapatite ceramic (mimics mineral bone)', excelos: '100% high-purity β-TCP' },
  { feat: 'Resorbability', fo: 'Resorbable HAP; curdlan degrades to glucose (metabolised)', kyphon: 'No data', bongros: 'No data', excelos: '100% biodegradable — replaced by autograft' },
  { feat: 'Animal / human tissue', fo: 'None', kyphon: 'None', bongros: 'None', excelos: 'None' },
  { feat: 'Immunological / toxic reaction', fo: 'None', kyphon: 'No data', bongros: 'No data', excelos: 'No data' },
  { feat: 'Osseointegration', fo: 'Excellent — osteoconductive scaffold; supports vascularization', kyphon: 'No data', bongros: 'Chemically fuses & grows into bone; preserves space for growth', excelos: 'Optimal osteoconduction; rapid bone in-growth' },
  { feat: 'Porosity', fo: 'Micro + macroporous; ~30 µm pores; 68–70%', kyphon: 'No data', bongros: '70–80% porous; ~300 µm pores', excelos: '~80% macroporosity; 100–300 µm pores' },
  { feat: 'Absorption capacity', fo: 'Absorbs blood/plasma → plastic, elastic & trimmable', kyphon: 'No data', bongros: 'No data', excelos: 'No data' },
  { feat: 'X-ray visibility', fo: 'Yes', kyphon: 'Yes', bongros: 'No data', excelos: 'No data' },
  { feat: 'Indications', fo: 'Small & large defects — orthopedics, traumatology, dental & veterinary', kyphon: 'Pathological vertebral fractures (kypho/vertebroplasty); tumours & metastases', bongros: 'Dental only — ridge augmentation, socket filling, sinus lift, periapical defects', excelos: 'Spine, joint revision, fracture repair, autograft extension, donor-site filler' },
];

const COMPARE_ADVANTAGES = [
  { vs: 'vs Kyphon® PMMA cement', text: 'FlexiOss® needs no special mixer or delivery device. PMMA cements cure exothermically — the local temperature spike can cause cell necrosis — and are prone to brittleness, depolymerisation, post-implant inflammation and reoperation from implant loosening. FlexiOss® avoids all of these.' },
  { vs: 'vs Bongros® HA chips', text: 'FlexiOss® is a flexible, cut-to-size implant rather than loose, crumbly granules, and serves orthopedics, dentistry and veterinary use — whereas Bongros HA is dental-only. FlexiOss® Dent can even be used without a membrane and soaked in medication (e.g. metronidazole).' },
  { vs: 'vs ExcelOs® β-TCP', text: 'FlexiOss® is supplied as a moldable, trimmable cylinder that absorbs blood and plasma for single-step intra-operative application, rather than loose β-TCP granulate — and spans orthopedic, dental and veterinary indications.' },
];

const ORTHO_CERTS = [
  { acr: 'CE', title: 'CE Certified', code: 'EC No. 1434-MDD-269/2021', desc: 'European conformity — highest safety standards' },
  { acr: 'FDA', title: 'PH-FDA Approved', code: 'CMDR-2025-04943A', desc: 'Certified for orthopedic applications in the Philippines' },
  { acr: 'PAT', title: 'Patent Protected', code: 'European Patents', desc: 'Protected across England, Austria, France, Spain, Ireland, Germany, Poland & Italy' },
];

const ORTHO_EMAIL_HREF = (() => {
  const url = `${SITE_URL}/flexioss-ortho`;
  const subject = 'FlexiOss® Ortho — synthetic bone-graft comparison';
  const body = [
    'Hi,',
    '',
    'Sharing a side-by-side comparison of FlexiOss® Ortho versus three leading bone-graft products (Kyphon® PMMA cement, Bongros® HA ceramic and ExcelOs® β-TCP):',
    '',
    ...COMPARE_ADVANTAGES.map((a) => `${a.vs} — ${a.text}`),
    '',
    `Full comparison matrix, certifications and peer-reviewed sources: ${url}`,
    '',
    'FlexiOss® is a third-generation hydroxyapatite + curdlan composite — bioactive, moldable after hydration, and slowly remodelled into natural bone with no implant-removal reoperation.',
  ].join('\n');
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
})();

const trackEmailComparison = () => {
  if (window.gtag) window.gtag('event', 'share_click', { method: 'email', content_type: 'ortho_comparison' });
};

const COMPARISON_TEXT = (() => {
  const lines = ['FlexiOss® Ortho — Bone-Graft Comparison', `${SITE_URL}/flexioss-ortho`, ''];
  COMPARE_ROWS.forEach((row) => {
    lines.push(row.feat.toUpperCase());
    COMPARE_COLS.forEach((c) => lines.push(`  • ${c.name}: ${row[c.key]}`));
    lines.push('');
  });
  lines.push('WHY FLEXIOSS®');
  COMPARE_ADVANTAGES.forEach((a) => lines.push(`  ${a.vs} — ${a.text}`));
  lines.push('', 'Comparative information is sourced from each manufacturer’s published documentation and provided for clinical reference.');
  return lines.join('\n');
})();

const copyComparison = async () => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(COMPARISON_TEXT);
    } else {
      const ta = document.createElement('textarea');
      ta.value = COMPARISON_TEXT;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    toast.success('Comparison copied to clipboard', {
      description: 'Paste it into an email, chat or document to share with a colleague.',
    });
    if (window.gtag) window.gtag('event', 'share_click', { method: 'copy', content_type: 'ortho_comparison' });
  } catch (e) {
    toast.error('Could not copy automatically — please select and copy the table manually.');
  }
};

const FlexiOssOrthoPage = () => {
  return (
    <SubpageLayout productName="Ortho">
      <StructuredData id="ortho-page" schema={orthoSchema} />
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-b from-slate-900 to-slate-950 overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/8 rounded-full blur-[120px]" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 mb-6"><span className="text-blue-400 font-bold text-sm">PH-FDA: CMDR-2025-04943A</span></div>
              <h1 className="text-4xl md:text-6xl font-black text-white mb-4 md:mb-6">FlexiOss<sup>®</sup> Ortho</h1>
              <p className="text-lg md:text-xl text-slate-300 mb-3 md:mb-4 leading-relaxed">Third-generation flexible bone substitute with TriCurean biphasic formula for orthopedic and traumatology procedures.</p>
              <p className="text-sm text-slate-400 mb-6 md:mb-8 leading-relaxed">Modern implantable biomaterial — biphasic (hydroxyapatite-polymeric), bioactive, supporting bone tissue regeneration and a potential drug carrier. After implantation, it integrates with patient's bone and is slowly remodelled into natural bone tissue, eliminating reoperation.</p>
              <div className="mb-6"><ReadAloud /></div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => dl(`${API}/api/document/ortho-ifu`, 'FlexiOss_Ortho_IFU.pdf')} className="bg-blue-600 hover:bg-blue-700 text-white font-bold" data-testid="ortho-dl-ifu"><Download className="w-4 h-4 mr-2" /> Download IFU</Button>
                <Button onClick={() => dl(`${API}/api/document/ortho-catalogue`, 'FlexiOss_Ortho_Catalogue.pdf')} variant="outline" className="border-blue-500 text-blue-400 hover:bg-blue-500/10 font-bold" data-testid="ortho-dl-brochure"><FileText className="w-4 h-4 mr-2" /> Product Catalogue</Button>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="relative group">
              <div className="absolute inset-0 bg-blue-500 rounded-3xl blur-3xl" style={{ opacity: 0.10, animation: 'subtle-glow 8s ease-in-out infinite' }} />
              <style>{`@keyframes subtle-glow { 0%,100%{opacity:0.10} 50%{opacity:0.18} }`}</style>
              <img src="/flexioss-ortho-transparent.png" alt="FlexiOss Ortho — PH-FDA certified hydroxyapatite-polymeric bone substitute for orthopedic trauma surgery" className="relative w-full h-auto drop-shadow-2xl transition-transform duration-500 group-hover:scale-105" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Videos */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-4xl font-black text-white mb-8 text-center">Clinical <span className="text-blue-400">Evidence</span></h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-slate-700 rounded-2xl overflow-hidden hover:border-blue-500 transition-all">
              <div className="aspect-video"><iframe width="100%" height="100%" src="https://www.youtube.com/embed/Y_He10shqJs" title="FlexiOss Ortho — Product Demonstration" frameBorder="0" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" loading="lazy" /></div>
              <div className="p-5"><h4 className="text-white font-bold text-base mb-1">Product Demonstration</h4><p className="text-slate-400 text-sm">Hydration, moldability, and surgical handling of FlexiOss® Ortho</p></div>
            </div>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-slate-700 rounded-2xl overflow-hidden hover:border-blue-500 transition-all">
              <div className="aspect-video"><iframe src="https://player.vimeo.com/video/1180794050?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479" width="100%" height="100%" frameBorder="0" allow="fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" title="FlexiOss Orthopedic — Clinical Trauma Surgery" className="w-full h-full" loading="lazy" /></div>
              <div className="p-5"><h4 className="text-white font-bold text-base mb-1">Clinical Video — Trauma Surgery</h4><p className="text-slate-400 text-sm">Live surgical application in comminuted fracture repair with FlexiOss® Ortho</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* PhilHealth Reimbursement */}
      <section className="bg-slate-950 px-6 lg:px-8 pt-4"><div className="mx-auto max-w-5xl"><PhilHealthCallout /></div></section>

      {/* Properties */}
      <ProductPropertiesEditorial
        properties={properties}
        testidPrefix="ortho"
        bg="bg-slate-950"
        heading={<>What Makes <span className="text-blue-400">FlexiOss® Ortho</span> Unique</>}
      />

      {/* Composition + Applications */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <div className="inline-block px-6 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 mb-4"><span className="text-blue-400 font-bold text-sm tracking-wider">TRICUREAN COMPOSITION</span></div>
              <h2 className="text-3xl font-black text-white mb-6">Biphasic Formula</h2>
              <p className="text-slate-300 leading-relaxed mb-8">The combination of two main compounds was obtained by polymer gelling into an irreversible, collagen-like triple helix — a conformation which does not evoke local inflammation and enables the entrapment of HAP granulate.</p>
              <EditorialColumnSplit
                accent={ACCENT}
                testid="ortho-composition"
                items={[
                  { term: 'Hydroxyapatite (HAP)', role: 'Ceramic Phase', def: <HorizontalBorderStack numbered={false} dense accent={ACCENT} testid="ortho-hap" items={['Low-sintered, highly porous ceramic', 'Fully synthetic — high chemical stability', 'Supports regeneration via enhanced ion uptake', 'Most stable form of calcium phosphate']} /> },
                  { term: 'Curdlan Polymer', role: 'Polymeric Phase', def: <HorizontalBorderStack numbered={false} dense accent={ACCENT} testid="ortho-curdlan" items={['Triple-helix structure (collagen-like)', 'Neutral for macrophages — no inflammation', 'Enables flexibility and high absorbency', 'Provides form stability']} /> },
                ]}
              />
            </div>
            <div>
              <div className="inline-block px-6 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 mb-4"><span className="text-blue-400 font-bold text-sm tracking-wider">AREAS OF APPLICATION</span></div>
              <h2 className="text-3xl font-black text-white mb-6">Clinical Applications</h2>
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-slate-700 rounded-2xl p-6 sm:p-8">
                <HorizontalBorderStack items={applications} accent={ACCENT} testid="ortho-applications" />
              </div>
              <div className="mt-6 grid grid-cols-2 gap-4">
                {[{ label: 'Orthopaedics', desc: 'Primary application area' }, { label: 'Musculoskeletal Trauma', desc: 'Emergency & reconstructive' }, { label: 'Dentistry', desc: 'Cyst & fistulae treatment' }, { label: 'Veterinary', desc: 'Companion animals' }].map((a, i) => (
                  <div key={i} className="bg-slate-800/60 border border-slate-700 rounded-xl p-4"><h4 className="text-white font-bold text-sm mb-1">{a.label}</h4><p className="text-slate-400 text-xs">{a.desc}</p></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <ProductCertifications testidPrefix="ortho" items={ORTHO_CERTS} bg="bg-slate-950" />

      {/* Comparative Analysis */}
      <section className="py-16 bg-slate-900" data-testid="ortho-comparison">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="mb-10 max-w-3xl">
            <div className="inline-block px-5 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/30 mb-5"><span className="text-blue-400 font-bold text-xs tracking-[0.18em] uppercase">Comparative Analysis</span></div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white">FlexiOss® vs <span className="text-blue-400">Leading Bone-Graft Products</span></h2>
            <p className="mt-4 text-sm sm:text-base text-slate-400">Side-by-side against a PMMA bone cement, a hydroxyapatite ceramic and a β-TCP graft — every figure drawn from each manufacturer's own published documentation.</p>
          </div>

          <div className="-mx-6 overflow-x-auto px-6 pb-2 sm:mx-0 sm:px-0" data-testid="ortho-comparison-scroll">
            <table className="w-full min-w-[940px] border-separate border-spacing-px overflow-hidden rounded-2xl bg-white/10 text-left">
              <caption className="sr-only">Comparison of FlexiOss Ortho versus Kyphon PMMA cement, Bongros HA ceramic and ExcelOs beta-TCP across eleven product attributes.</caption>
              <thead>
                <tr>
                  <th scope="col" className="bg-slate-950 px-5 py-4 align-bottom text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Feature</th>
                  {COMPARE_COLS.map((c) => (
                    <th key={c.key} scope="col" className={`px-5 py-4 align-bottom ${c.hero ? 'bg-blue-600' : 'bg-slate-950'}`}>
                      <span className={`block text-sm font-bold leading-tight ${c.hero ? 'text-blue-50' : 'text-white'}`}>{c.name}</span>
                      <span className={`mt-1 block text-[11px] font-medium leading-tight ${c.hero ? 'text-blue-100/80' : 'text-slate-500'}`}>{c.sub}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row, ri) => (
                  <motion.tr
                    key={ri}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.45, delay: Math.min(ri * 0.035, 0.3), ease: [0.16, 1, 0.3, 1] }}
                  >
                    <th scope="row" className="bg-slate-900 px-5 py-4 align-top text-sm font-semibold text-white">{row.feat}</th>
                    {COMPARE_COLS.map((c) => {
                      const val = row[c.key];
                      const noData = val === 'No data';
                      return (
                        <td key={c.key} className={`px-5 py-4 align-top ${c.hero ? 'bg-blue-500/[0.07]' : 'bg-slate-900'}`}>
                          {noData
                            ? <span className="select-none text-2xl leading-none text-slate-600" title="Not stated in manufacturer documentation" aria-label="Not stated in manufacturer documentation">—</span>
                            : <span className={`text-sm leading-relaxed ${c.hero ? 'font-medium text-blue-400' : 'text-slate-300'}`}>{val}</span>}
                        </td>
                      );
                    })}
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 max-w-5xl text-xs leading-relaxed text-slate-500">
            <span className="text-slate-400">—</span>&nbsp; indicates the specification was not stated in that product's published manufacturer documentation. Comparative information is sourced from manufacturer materials and provided for clinical reference.
          </p>

          {/* Share — copy or email this comparison to a colleague */}
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={copyComparison}
              data-testid="ortho-copy-comparison"
              className="group inline-flex items-center gap-2.5 rounded-full border border-blue-500/40 bg-blue-500/10 px-5 py-2.5 text-sm font-bold text-blue-400 transition-all duration-300 hover:border-blue-400 hover:bg-blue-500/20 hover:text-blue-300"
            >
              <Copy className="h-4 w-4 transition-transform group-hover:-translate-y-0.5" strokeWidth={2.2} />
              Copy comparison
            </button>
            <a
              href={ORTHO_EMAIL_HREF}
              onClick={trackEmailComparison}
              data-testid="ortho-email-comparison"
              className="group inline-flex items-center gap-2.5 rounded-full border border-blue-500/40 bg-blue-500/10 px-5 py-2.5 text-sm font-bold text-blue-400 transition-all duration-300 hover:border-blue-400 hover:bg-blue-500/20 hover:text-blue-300"
            >
              <Mail className="h-4 w-4 transition-transform group-hover:-translate-y-0.5" strokeWidth={2.2} />
              Email this comparison to a colleague
            </a>
            <span className="basis-full text-xs text-slate-500 sm:basis-auto">Copies a procurement-ready summary with sources — no data leaves your device.</span>
          </div>

          {/* Document-sourced advantages — asymmetric indexed editorial blocks */}
          <div className="mt-16 border-t border-white/10">
            {COMPARE_ADVANTAGES.map((a, i) => (
              <div key={i} data-testid={`ortho-advantage-${i}`} className="grid gap-5 border-b border-white/10 py-10 md:grid-cols-12 md:gap-10">
                <div className="flex items-start gap-4 md:col-span-4">
                  <span className="text-6xl font-black leading-none tabular-nums text-blue-500/20 md:text-7xl">{String(i + 1).padStart(2, '0')}</span>
                  <span className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-blue-400">{a.vs}</span>
                </div>
                <div className="md:col-span-8 md:border-l md:border-white/10 md:pl-10">
                  <p className="text-base leading-relaxed text-slate-300 md:text-lg">{a.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Publications */}
      <ProductPublications publications={publications} dl={dl} testidPrefix="ortho" bg="bg-slate-950" />

      {/* CTA */}
      <section className="py-20 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-black text-white mb-6">Ready to Experience FlexiOss<sup>®</sup> Ortho?</h2>
          <p className="text-xl text-slate-300 mb-8">Contact our sales team for product information, pricing, and ordering.</p>
          <Link to="/"><Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-12 py-6 text-xl shadow-2xl shadow-blue-600/50 hover:scale-105 transition-all" data-testid="ortho-cta">Request for Samples <ChevronRight className="ml-2 w-5 h-5" /></Button></Link>
          <div className="mt-12"><SocialShare pageKey="ortho" accent={ACCENT} /></div>
        </div>
      </section>
    </SubpageLayout>
  );
};

export default FlexiOssOrthoPage;
